import { AclResourceNode } from '../../../../types/entities';

/**
 * What ticking and unticking a box in the role tree does to the stored grant list.
 *
 * Pulled out of the component because it is the only part of that screen with a rule in it, and
 * because the rule is one the screen cannot show you is wrong. A grant list that says something
 * different from what the boxes appear to say produces a role that quietly reaches more than the
 * admin who saved it believes, and nothing anywhere reports that.
 */

/** Every id at or beneath this node, which is what ticking a parent has to cover. */
export const descendantIds = (node: AclResourceNode): string[] =>
    [node.id, ...(node.children ?? []).flatMap(descendantIds)];

/**
 * Whether this node is a verb - view, create, edit or delete - rather than a section.
 *
 * The server marks them: `AclResourceProvider` generates them from a section's `verbs` key and
 * puts the verb on the node. So the browser never has to parse an id to know what it is looking
 * at, and a change to the id format cannot break the role screen.
 */
export const isVerb = (node: AclResourceNode): boolean => typeof node.verb === 'string';

/**
 * The stored grant list after a box changes.
 *
 * **Unticking removes the node's ancestors as well as its descendants, and that is the whole
 * point of this function.** Granting a resource grants everything beneath it, so a selection that
 * still holds `HQ_Sales::sales` goes on granting `HQ_Sales::coupons` however plainly the Coupons
 * box appears to be unticked. Before verbs existed this was already wrong - unticking a section
 * under a ticked group left it granted - and it was invisible, because the box did what it was
 * told and the server did something else. Verbs make it unmissable: the entire feature is
 * withholding `delete` from a role that has the rest of a section, and that is exactly the case
 * a surviving ancestor silently undoes.
 *
 * Nothing is lost by dropping the ancestors, because ticking a parent stores every descendant
 * explicitly rather than relying on inheritance to fill them in. So removing `HQ_Sales::sales`
 * leaves every section beneath it still individually granted, minus the one just unticked.
 *
 * Ticking adds the node and everything beneath it, filtered to what this admin may actually hand
 * out. Without that filter, ticking a parent would put resources into the payload HQ-API refuses,
 * and the save would fail naming a box the admin never clicked.
 *
 * @param selected  the grant list as it stands
 * @param node      the node whose box changed
 * @param ancestors ids of the nodes above it, root first
 * @param checked   what the box changed to
 * @param grantable what the signed-in admin is entitled to grant to somebody else
 */
export const applyToggle = (
    selected: string[],
    node: AclResourceNode,
    ancestors: string[],
    checked: boolean,
    grantable: string[]
): string[] => {
    const affected: string[] = descendantIds(node);

    if (!checked) {
        return selected.filter(
            (id: string): boolean => !affected.includes(id) && !ancestors.includes(id)
        );
    }

    const granting: string[] = affected.filter((id: string): boolean => grantable.includes(id));

    return Array.from(new Set([...selected, ...granting]));
};
